import { Color } from '@persona-id/design-tokens';
import { FC, ReactNode } from 'react';
import { matchPath, useLocation } from 'react-router';
import styled from 'styled-components';

import Navbar, { NAVBAR_COLLAPSED_WIDTH, NAVBAR_EXPANDED_WIDTH } from 'dashboard/components/Navbar';
import { openSupportChat } from 'dashboard/components/SupportChat';
import { Environment } from 'dashboard/models/environment';
import { Organization } from 'dashboard/models/organization';
import { Z_INDICES } from 'dashboard/styles/variables';
import { TransitionDuration } from 'design-system/variables/transitions';
import useNavbarToggle from 'lib/hooks/useNavbarToggle';
import useToggle from 'lib/hooks/useToggle';

const PaddedContainer = styled('div')<{ isCollapsed: boolean }>`
  position: relative;
  padding-left: ${({ isCollapsed }) =>
    isCollapsed ? `${NAVBAR_COLLAPSED_WIDTH}px` : `${NAVBAR_EXPANDED_WIDTH}px`};
  transition-duration: ${({ isCollapsed }) =>
    isCollapsed ? TransitionDuration.Medium : TransitionDuration.Fast};

  @media print {
    padding-left: 0;
  }

  height: 100%;
`;

export const ROUTES_TO_HIDE_NAVBAR: string[] = [
  '/dashboard/password-reset',
  '/dashboard/user/accept-invite',
  '/dashboard/login',
  '/dashboard/signup',
  '/dashboard/document-viewer',
  '/dashboard/cases/:caseId/print',
];

interface WithNavbarProps {
  children: ReactNode;
  isLoggedIn: boolean;
  environments: Environment[];
  organization: Organization;
}

const WithNavbar: FC<WithNavbarProps> = ({ children, isLoggedIn }: WithNavbarProps) => {
  const location = useLocation();

  const showNavBar =
    isLoggedIn &&
    ROUTES_TO_HIDE_NAVBAR.every((route) => !matchPath(location.pathname, { path: route }));

  const [isCollapsed, { toggleNavbarCollapsed }] = useNavbarToggle();
  const [isEditing, { toggle: toggleEdit }] = useToggle(false);

  return showNavBar ? (
    <PaddedContainer isCollapsed={isCollapsed}>
      <Navbar
        isCollapsed={isCollapsed}
        isEditing={isEditing}
        toggleCollapse={toggleNavbarCollapsed}
        toggleEdit={toggleEdit}
        openSupportChat={openSupportChat}
      />
      <StyledMain role='main' id='main'>
        {isEditing && <StyledEditNavbarOverlay />}
        {children}
      </StyledMain>
    </PaddedContainer>
  ) : (
    <main role='main' id='main'>
      {children}
    </main>
  );
};
export default WithNavbar;

const StyledEditNavbarOverlay = styled.div`
  position: absolute;
  background-color: ${Color.PrimaryBlack};
  height: 100%;
  width: 100%;
  z-index: ${Z_INDICES.modal};
  opacity: 80%;
`;

const StyledMain = styled.main`
  position: relative;
  min-height: 100%;
`;
